import { api } from './baseAPI';
import {
  ApiResponse,
  Teacher,
  User,
  ListResponse
} from '../types/api';

// 프로필 캐시 관리
let profileCache: Map<string, { data: User | Teacher; timestamp: number }> = new Map();
const PROFILE_CACHE_DURATION = 5 * 60 * 1000; // 5분

// 통합 사용자 API (사용자 + 선생님)
export const userAPI = {
  // === 공통 기능 ===

  // 프로필 조회 (통합, 캐싱 적용)
  getProfile: async (): Promise<ApiResponse<User | Teacher>> => {
    try {
      // 캐시된 프로필이 유효한지 확인
      const now = Date.now();
      const cacheKey = 'current-user-profile';
      const cachedProfile = profileCache.get(cacheKey);

      if (cachedProfile && (now - cachedProfile.timestamp) < PROFILE_CACHE_DURATION) {
        return {
          success: true,
          message: '캐시된 프로필 정보',
          data: cachedProfile.data
        };
      }

      // 캐시가 없거나 만료된 경우에만 API 호출
      const response = await api.get('/auth/verify');

      if (response.data.success && response.data.data) {
        const { user } = response.data.data;

        // 응답 데이터를 ApiResponse<User | Teacher> 형태로 변환
        const result = {
          success: true,
          message: response.data.message,
          data: user
        };

        // 성공한 응답을 캐시에 저장
        profileCache.set(cacheKey, {
          data: user,
          timestamp: now
        });

        return result;
      }

      throw new Error(response.data.message || '사용자 정보를 가져올 수 없습니다.');
    } catch (error) {
      throw error;
    }
  },

  // 프로필 캐시 무효화 (사용자 정보 업데이트 시 호출)
  invalidateProfileCache: () => {
    profileCache.clear();
  },

  // 비밀번호 변경 (통합)
  changePassword: async (passwords: { currentPassword: string; newPassword: string }): Promise<ApiResponse<void>> => {
    const response = await api.put('/users/change-password', passwords);
    return response.data;
  },

  // === 사용자 관련 기능 ===

  // 사용자 목록 조회
  getUsers: async (params?: { status?: string; limit?: number; offset?: number; searchField?: string }): Promise<ApiResponse<ListResponse<User>>> => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  // 사용자 상세 조회
  getUser: async (userId: string): Promise<ApiResponse<User>> => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  // 사용자 정보 수정
  updateUser: async (userId: string, userData: Partial<User>): Promise<ApiResponse<User>> => {
    const response = await api.patch(`/users/${userId}`, userData);
    return response.data;
  },

  // 사용자 삭제
  deleteUser: async (userId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  // 오늘 결제 예정자 조회
  getTodayPaymentDueUsers: async (): Promise<ApiResponse<{ users: User[]; count: number; date: string }>> => {
    const response = await api.get('/users/payment-due/today');
    return response.data;
  },

  // 회원 비밀번호 초기화 (연락처로)
  resetUserPassword: async (userId: string): Promise<ApiResponse<void>> => {
    const response = await api.post(`/users/${userId}/reset-password`);
    return response.data;
  },

  // 비활성 회원 재활성화
  reactivateUser: async (userId: string, lessonData: {
    lessonDayOfWeek: string;
    lessonStartTime: string;
    lessonEndTime: string;
  }): Promise<ApiResponse<User>> => {
    const response = await api.post(`/users/${userId}/reactivate`, lessonData);
    return response.data;
  },

  // 이메일 수정 요청
  requestEmailChange: async (emailData: { newEmail: string; reason?: string }): Promise<ApiResponse<void>> => {
    const response = await api.post('/users/request-email-change', emailData);
    return response.data;
  },

  // 이메일 수정
  updateEmail: async (email: string): Promise<ApiResponse<{ email: string }>> => {
    const response = await api.put('/users/email', { email });
    return response.data;
  },

  // === 선생님 관련 기능 ===

  // 선생님 목록 조회
  getTeachers: async (): Promise<ApiResponse<ListResponse<Teacher>>> => {
    const response = await api.get('/teachers');
    return response.data;
  },

  // 선생님 정보 수정
  updateTeacher: async (teacherId: string, teacherData: Partial<Teacher>): Promise<ApiResponse<Teacher>> => {
    const response = await api.patch(`/teachers/${teacherId}`, teacherData);
    return response.data;
  },

  // 선생님 상세 조회
  getTeacher: async (teacherId: string): Promise<ApiResponse<Teacher>> => {
    const response = await api.get(`/teachers/${teacherId}`);
    return response.data;
  },

  // 선생님 삭제
  deleteTeacher: async (teacherId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/teachers/${teacherId}`);
    return response.data;
  },
};

// 공개 API (인증 불필요)
const publicAPI = {
  // 수업 신청용 선생님 목록 조회 (간단한 정보만)
  getTeachersForBooking: async (): Promise<ApiResponse<{ teachers: Array<{ id: number; teacherId: string; name: string }> }>> => {
    const response = await api.get('/teachers/for-booking');
    return response.data;
  },
};

// 레거시 호환성을 위한 별칭들
export const teacherAPI = {
  getTeachers: userAPI.getTeachers,
  updateTeacher: userAPI.updateTeacher,
  getTeacher: userAPI.getTeacher,
  deleteTeacher: userAPI.deleteTeacher,
  getTeachersForBooking: publicAPI.getTeachersForBooking,

  // pending 개수 통합 조회 (Header용) - 캐싱 적용
  getPendingCounts: async (): Promise<ApiResponse<{
    lessonRequests: number;
    oneDayRequests: number;
    contacts: number;
    modifications: number;
    cancellations: number;
  }>> => {
    // 캐시 키
    const cacheKey = 'pending-counts';
    const now = Date.now();
    const cacheExpiry = 5000; // 5초 캐시 (더 자주 업데이트)

    // 캐시된 데이터가 있고 유효한 경우 반환
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (now - timestamp < cacheExpiry) {
          return { data, success: true, message: '캐시된 데이터' };
        }
      } catch (e) {
        // 캐시 파싱 실패 시 무시
      }
    }

    // API 호출
    const response = await api.get('/teachers/pending-counts');

    // 성공한 응답을 캐시에 저장
    if (response.data?.success) {
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: response.data.data,
        timestamp: now
      }));
    }

    return response.data;
  },

  // pending counts 캐시 무효화 (데이터 변경 시 호출)
  invalidatePendingCountsCache: () => {
    sessionStorage.removeItem('pending-counts');
  },
};

export default userAPI;